"use client";

/**
 * DropdownMenu Component
 *
 * A composable dropdown menu component with support for icons, dividers, and variants.
 *
 * @example
 * ```tsx
 * <DropdownMenu closeAfterClick>
 *   <DropdownMenuTrigger asChild>
 *     <button>Open Menu</button>
 *   </DropdownMenuTrigger>
 *
 *   <DropdownMenuContent align="right">
 *     <DropdownMenuItem icon={<User />} onClick={handleProfile}>
 *       Profile
 *     </DropdownMenuItem>
 *     <DropdownMenuDivider />
 *     <DropdownMenuItem icon={<LogOut />} onClick={handleSignOut} variant="danger">
 *       Sign Out
 *     </DropdownMenuItem>
 *   </DropdownMenuContent>
 * </DropdownMenu>
 * ```
 */
import { cn, onBtnClick } from "@garther/ui/libs";
import React, { createContext, useContext } from "react";

import { useDropdown } from "../../../hooks/use-dropdown";

interface DropdownMenuContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  closeAfterClick: boolean;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | undefined>(undefined);

function useDropdownMenuContext() {
  const context = useContext(DropdownMenuContext);
  if (!context) {
    throw new Error("DropdownMenu compound components must be used within DropdownMenu");
  }
  return context;
}

interface DropdownMenuProps {
  children: React.ReactNode;
  closeAfterClick?: boolean;
  className?: string;
}

export function DropdownMenu({ children, className, closeAfterClick = true }: DropdownMenuProps) {
  const { isOpen, setIsOpen, dropdownRef } = useDropdown({ closeAfterClick });

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen, closeAfterClick }}>
      <div className={cn("relative", className)} ref={dropdownRef}>
        {children}
        {/* Backdrop overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={onBtnClick(() => setIsOpen(false))}
            aria-hidden="true"
          />
        )}
      </div>
    </DropdownMenuContext.Provider>
  );
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

export function DropdownMenuTrigger({ children, className, asChild = false }: DropdownMenuTriggerProps) {
  const { isOpen, setIsOpen } = useDropdownMenuContext();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toggleDropdown();
      },
      className: cn((children as React.ReactElement<any>).props.className, className)
    });
  }

  return (
    <button onClick={toggleDropdown} className={className} type="button">
      {children}
    </button>
  );
}

interface DropdownMenuContentProps {
  children: React.ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
  width?: string;
}

export function DropdownMenuContent({
  children,
  className,
  align = "right",
  width = "w-[195px]"
}: DropdownMenuContentProps) {
  const { isOpen } = useDropdownMenuContext();

  if (!isOpen) return null;

  const alignmentClasses = {
    left: "left-0",
    right: "right-0",
    center: "left-1/2 -translate-x-1/2"
  };

  return (
    <div
      className={cn(
        "absolute mt-2 bg-background-primary backdrop-blur-md rounded-sm border border-border-light shadow-xl z-50 text-responsive-base",
        width,
        alignmentClasses[align],
        className
      )}
    >
      <div className="py-1">{children}</div>
    </div>
  );
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  variant?: "default" | "danger";
  className?: string;
  disabled?: boolean;
}

export function DropdownMenuItem({
  children,
  icon,
  onClick,
  variant = "default",
  className,
  disabled = false
}: DropdownMenuItemProps) {
  const variantClasses = {
    default: "text-text-primary hover:bg-background-highlight",
    danger: "text-red-500 hover:bg-red-500/10"
  };

  return (
    <button
      onClick={onBtnClick(() => onClick && onClick())}
      disabled={disabled}
      className={cn(
        "w-full text-left px-4 py-2.5 transition-colors duration-200 flex items-center space-x-3",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantClasses[variant],
        className
      )}
      type="button"
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </button>
  );
}

interface DropdownMenuDividerProps {
  className?: string;
}

export function DropdownMenuDivider({ className }: DropdownMenuDividerProps) {
  return <div className={cn("border-t border-border-light my-1", className)} />;
}

interface DropdownMenuLabelProps {
  children: React.ReactNode;
  className?: string;
}

export function DropdownMenuLabel({ children, className }: DropdownMenuLabelProps) {
  return (
    <div className={cn("px-4 py-2 text-xs text-text-secondary font-medium uppercase", className)}>
      {children}
    </div>
  );
}
